<!--
    ONE plain section: a titled band in the section's own hue, its Basisdaten, one expander per run
    for the Weitere Angaben, and what is not on screen.

    Chrome, spacing, type scale and colour are PrimeFlex utilities and theme tokens — including the
    hue, which is a pair of PrimeFlex utilities picked in TypeScript from the sheet's one hue map. The
    component's own SCSS carries only what a utility cannot express.

    Every value is printed exactly as the server sent it: nothing here is rounded, re-derived,
    re-formatted or currency-mapped, and no pair is drawn by this template — they all go through
    mvs-cm-ed-kv-list, which is also where the LEVEL LADDER is defined.
-->
@if (section) {

    <div class="surface-card border-1 surface-border border-round" [ngClass]="padClass">

        <!--
            Header. align-items-stretch lets the accent spine span the header's full height without a
            hard-coded height, exactly as on the Deckung card, so a heading that grows with the app's
            font scale keeps its bar.
        -->
        <div class="flex align-items-stretch gap-2">

            <!--
                The spine states WHICH SECTION — Vertrag, Beitrag, Personen, Objekte — or, for a
                Sparte, the accent of the Deckung cards built from it. Every section carries one now;
                see spineClass for why the "no bar without a Sparte" rule no longer applies.
            -->
            <span class="cm-ed-accent border-round flex-none"
                  [ngClass]="spineClass"
                  aria-hidden="true"></span>

            <div class="flex flex-1 min-w-0 align-items-baseline gap-2">

                <!--
                    THE ellipsis. A partner-supplied heading is of unbounded length and must be cut,
                    never wrapped: a two-line heading pushes everything under it out of alignment with
                    the cards beside it.

                    min-w-0 is load-bearing — a flex child defaults to min-width:auto and refuses to
                    shrink below its content, so text-overflow-ellipsis alone would do nothing here.
                    It no longer carries `flex-1`, because the RULE beside it takes the free width;
                    a flex item shrinks by default, so the ellipsis still bites on a long heading.
                    title keeps the full heading reachable on hover.

                    A SECTION heading, not a card title: the section's HUE + uppercase + the sheet's
                    small-caps tracking (see .cm-ed-bs-h). The two are different kinds of thing and
                    look it — a card names ONE delivered object and stays `text-color`, while a
                    section names a FAMILY of them and is a landmark a reader navigates by, in the
                    same colour as its spine. Uppercase widens the string by roughly a tenth, which is
                    why the ellipsis and the `title` above are not optional here.

                    THE HEADING ITSELF CAN BE THE HIT. The host counts a section's label and sub into
                    the sheet's total, and until now this component could not see that — a reader
                    searching "Hausrat" watched the header's number rise over a page on which nothing
                    changed. `titleHit` reads the section's own roll-up (see the component) and the
                    heading takes `.cm-ed-hit-title`, the sheet's one treatment for "the term is in
                    this heading" — used unaltered by the row heading below, by the three cards and by
                    nothing else.

                    Marked as a WHOLE, not run by run: the host splits labels and values into
                    highlight runs and splits no heading, so which characters matched is not knowledge
                    this component has and marking a guess would be worse than marking the heading.

                    The mark sits on an INNER span, and that is the sheet's ONE mechanism for a marked
                    heading: the class always wraps exactly the text, never the box around it. Two
                    things follow from it and both are load-bearing. The hue utility stays on the outer
                    element and the mark still wins its own colour, because an inherited value always
                    loses to the element's own declaration, `!important` on the parent included — and
                    it must win: `text-green-600` on the hit wash measures 2.48:1, far under any floor,
                    where the body colour gives 7.78:1. Nothing about "which section is this" is lost,
                    because the accent SPINE beside the heading states the same hue and no search
                    touches it. And the wash hugs the words instead of stretching across whatever width
                    the outer element happens to own.
                -->
                <span class="cm-ed-bs-h font-semibold uppercase min-w-0 overflow-hidden text-overflow-ellipsis white-space-nowrap"
                      [ngClass]="[titleClass, headingClass]"
                      [title]="section.label"><span [class.cm-ed-hit-title]="titleHit">{{section.label}}</span></span>

                <!--
                    The rule that carries the heading across the section's full width, so the block
                    reads as a titled band rather than as a line of text with white space after it.
                    It TAKES the free width (flex-1) — which is what pins the sub line to the right
                    edge now that the row no longer needs justify-content-between — and it shrinks to
                    nothing when the heading and the sub already fill the row.

                    align-self-center keeps it on the optical middle of the heading while the row
                    itself stays baseline-aligned, which is what keeps the heading and its sub on one
                    line. Purely decorative, so aria-hidden.
                -->
                <span class="flex-1 border-top-1 surface-border align-self-center" aria-hidden="true"></span>

                <!--
                    The sub line is dropped at xs — now BELOW 560 px, where the old ladder put that
                    floor at 420 px: in a panel that narrow the sub takes the width the heading needs,
                    and the heading is what identifies the section. It is the only thing this sheet
                    drops for width, and it stays tied to the floor of the ladder rather than creeping
                    up it: from `sm` on the section is full width and both fit.
                -->
                @if (section.sub && size !== 'xs') {
                    <!--
                        The sub is part of the HEADING the host matched: `countTextHits(label, sub)`
                        counts the two together and publishes one total, so which of them carried the
                        term is not knowledge this component has. It therefore takes the same mark as
                        the label whenever the heading matched — the same statement the row heading
                        below makes over its own title and sub, and the honest one: "the term is in
                        this heading", never a guess at which half.

                        Inner span, as everywhere: the muted utility stays on the outer element and the
                        mark's own colour still wins, which it must — a marked sub that kept the muted
                        tone measures 3.58:1 on the hit wash against the body colour's 7.78:1.
                    -->
                    <span class="text-xs text-color-secondary white-space-nowrap"><span [class.cm-ed-hit-title]="titleHit">{{section.sub}}</span></span>
                }

                <!--
                    WHAT THIS SECTION HOLDS FOR THE CURRENT TERM, and only what a reader can reach
                    HERE: its own HEADING, its Basisdaten and its Weitere Angaben, never the level-3
                    fields behind "{n} nicht angezeigt". Those are unreachable in this section at any
                    expander setting, and the sheet's own header states them separately — a count
                    claimed in two places is a count that will eventually be claimed twice.

                    The heading's share joined this number with the mark above it, deliberately in one
                    change: a marker that counted a heading it did not mark, or marked a heading it did
                    not count, is the same broken promise seen from either end.

                    It carries real weight now that nothing dims: a Beitrag block holding two matches
                    is visually identical to one holding none, and this marker is the only thing that
                    says otherwise. Rendered only while a term is active and only when there is
                    something to declare — a "0 Treffer" on every section would bury the section that
                    has hits.

                    NOT dropped at `xs`, unlike the sub line beside it: the sub is context a narrow
                    panel can spare, an unread hit is what the reader came for.
                -->
                @if (searching && hitCount) {
                    <span class="cm-ed-hits bg-primary-50 text-primary border-1 border-primary border-round-sm white-space-nowrap">{{hitCount}} Treffer</span>
                }

            </div>

        </div>

        <!--
            The section's RUNS: its own pairs, then one per delivered row (a Beitrag's price slices,
            an unrecognised family's rows).

            ONE template branch for both, so the section's own figures and a price slice can never
            grow different behaviour — the drift that had the premium block rendering ~36 pairs flat
            while every card beside it showed four and hid the rest.

            Tracked by the run's own stable anchor and never by position, so a re-read that adds or
            removes one row re-uses the DOM of the rows that did not change.
        -->
        @for (run of runs; track run.key; let first = $first) {

            <!--
                The separator between runs. The first run follows the header, which already draws the
                boundary; every further one gets a rule of its own. Bound rather than switched off in
                SCSS, because PrimeFlex marks its border utilities !important and a "first run has no
                rule" declaration would not win against a class that is always applied.
            -->
            <div class="mt-2"
                 [class.pt-2]="!first"
                 [class.border-top-1]="!first"
                 [class.surface-border]="!first">

                <!--
                    The row's own heading, rendered whenever the row has anything to say about itself.
                    A family whose rows carry no title at all therefore stays free of empty heading
                    lines, and the section's own run never renders one — the section heading above is
                    its title.

                    THE ROW'S OWN TEXT MATCHED — its title, its sub or one of the relations it
                    carries. `runTitleHit` is true only for the SURPLUS the run's pairs do not account
                    for, so a row whose figures matched is marked by those figures and not twice here,
                    while a row matched by name alone ("Anna Meier") is still visibly a hit instead of
                    a heading with an unexplained number beside it. The host splits labels and values
                    into runs, not headings, so the heading is marked as a whole.

                    The mark sits on the TEXTS and no longer on the row: `.cm-ed-hit-title`, the
                    sheet's one treatment for a marked heading, used here exactly as the section
                    heading above and the three card titles use it. It was a `bg-primary-50` wash over
                    the whole row, which stretched the highlight across the empty width beside a
                    two-word heading and used the PAIR's step for something that is not a pair — two
                    reasons the same rewrite removed at once.

                    Inner spans, as everywhere the sheet marks a heading: the muted utility stays on
                    the outer element and the mark's own colour still wins, which it must — a marked
                    heading that kept the muted tone measures 3.58:1 on the hit wash where the body
                    colour gives 7.78:1.
                -->
                @if (run.title || run.sub) {
                    <div class="flex align-items-baseline flex-wrap gap-2 mb-1">
                        @if (run.title) {
                            <span class="cm-ed-bs-row-h text-color-secondary"><span [class.cm-ed-hit-title]="runTitleHit(run)">{{run.title}}</span></span>
                        }
                        @if (run.sub) {
                            <span class="text-xs text-color-secondary"><span [class.cm-ed-hit-title]="runTitleHit(run)">{{run.sub}}</span></span>
                        }
                    </div>
                }

                <!--
                    BASISDATEN — displayLevel 1, always on screen. The run label is offered to the
                    section's own run only: a view that stacks several sections of different levels
                    names the first run through [runLabel], while a row already carries its heading.
                -->
                @if (run.basis.length) {
                    <mvs-cm-ed-kv-list [fields]="run.basis"
                                       [size]="size"
                                       [layout]="layout"
                                       [emphasis]="emphasis"
                                       [runLabel]="first ? runLabel : null"
                                       [searching]="searching"></mvs-cm-ed-kv-list>
                }

                <!--
                    The expander, and beside it what neither the run nor the expander shows.

                    Deliberately the Deckung card's control, word for word: a p-button so Space and
                    Enter activate it and it carries the theme's focus ring, the theme's primary
                    colour because it is an ACTION, rendered only when there is something behind it,
                    and never persisted. One expander idiom for the whole sheet — a section that
                    opened differently from the card next to it would have to be learned twice.

                    aria-expanded / aria-controls travel through [pt].root, the only way onto the
                    element that actually carries the button role.

                    The count stays SECONDARY: it is not clickable, and giving it the action colour
                    would offer a control that does not exist. It is stated rather than dropped
                    because a reader who expands the run and STILL does not find their field would
                    otherwise conclude it was never delivered — the "es wurde nicht importiert" claim
                    this screen was built to refute.
                -->
                @if (run.weitere.length || run.restCount) {
                    <div class="flex flex-wrap align-items-center gap-2 mt-1">

                        <!--
                            The label follows the run's EFFECTIVE open state, not its stored one:
                            while a term is active the block may already be on screen because the
                            search put it there, and "3 weitere Angaben →" over pairs that are visible
                            is a control that lies about what it does.
                        -->
                        @if (run.weitere.length) {
                            <p-button [label]="runOpen(run) ? 'Weniger anzeigen' : run.weitere.length + ' weitere Angaben →'"
                                      size="small"
                                      [text]="true"
                                      styleClass="cm-ed-more"
                                      [pt]="foldPtOf(run)"
                                      (onClick)="toggle(run)"></p-button>
                        }

                        @if (run.restCount) {
                            <span class="text-xs text-color-secondary white-space-nowrap">{{run.restCount}} nicht angezeigt</span>
                        }

                    </div>
                }

                <!--
                    The controlled region is ALWAYS in the document and only its content is
                    conditional: an aria-controls pointing at an element that does not exist yet is a
                    dangling reference for assistive technology.

                    WEITERE ANGABEN — announced by the run label rather than by a bare divider, so the
                    reader is told what changed and not merely that something did. The label carries
                    its own rule, which is why this block adds no border of its own.

                    AUTO-REVEAL. `runOpen` is the reader's own state OR, while a term is active, the
                    search having found one of its matches inside these very pairs. A match behind a
                    closed expander is a match the reader is never shown — and with nothing dimmed, an
                    unopened run would look exactly like a run with nothing in it.

                    The run label stays exactly as it is: the reveal changes WHETHER these pairs are on
                    screen, never how they are announced. Temporary, and never written back — clearing
                    the term returns the run to the state its reader left it in.
                -->
                <div [attr.id]="run.bodyId">
                    @if (runOpen(run)) {
                        <div class="mt-1">
                            <mvs-cm-ed-kv-list [fields]="run.weitere"
                                               [size]="size"
                                               [layout]="layout"
                                               emphasis="weitere"
                                               runLabel="Weitere Angaben"
                                               [searching]="searching"></mvs-cm-ed-kv-list>
                        </div>
                    }
                </div>

            </div>
        }

        <!--
            The family arrived and carried nothing. Said out loud, because for an unrecognised family
            the heading is the news, and a heading over an empty box reads like a section that failed
            to load — on a screen whose whole purpose is to prove nothing went missing.
        -->
        @if (empty && !section.restCount) {
            <div class="text-xs text-color-secondary mt-2">Keine Angaben in dieser Lieferung</div>
        }

    </div>
}
